import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import Header from "./header";
import Footer from "./footer";
import RandomTopics from "./randompost";
import apiUrl from "./base_url";

import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Spinner,
  Dropdown,
  Button,
} from "react-bootstrap";

import "../components/cssfile/mcqp.css";

const MCQ = () => {
  const location = useLocation();

  const initialSecId =
    location.state?.sec_id || localStorage.getItem("sec_id");

  const initialId =
    location.state?.id || localStorage.getItem("id");

  const initialSectionName =
    location.state?.section_name ||
    localStorage.getItem("section_name");

  const initialTopic =
    location.state?.topic_name ||
    localStorage.getItem("topic_name");

  const [sec_id, setSecId] = useState(initialSecId);
  const [id, setId] = useState(initialId);
  const [section_name, setSectionName] = useState(initialSectionName);
  const [topic_name, setTopicName] = useState(initialTopic);

  const [questions, setQuestions] = useState([]);
  const [sections, setSections] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedSection, setSelectedSection] = useState(sec_id);

  /*
   * ---------------------------------------------------------
   * SAVE CURRENT SECTION INFORMATION
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (sec_id && id && section_name && topic_name) {
      localStorage.setItem("sec_id", sec_id);
      localStorage.setItem("id", id);
      localStorage.setItem("section_name", section_name);
      localStorage.setItem("topic_name", topic_name);
    }
  }, [sec_id, id, section_name, topic_name]);

  /*
   * ---------------------------------------------------------
   * FETCH QUESTIONS
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (!sec_id) {
      setError("Invalid section ID.");
      setLoading(false);
      return;
    }

    const fetchQuestions = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${apiUrl}serverside/mcq_practice_question.php?sec_id=${sec_id}`
        );

        setQuestions(response.data || []);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch questions. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [sec_id]);

  /*
   * ---------------------------------------------------------
   * FETCH SECTIONS
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (!id) return;

    const fetchSections = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}serverside/mcq_practice_section.php`
        );

        const filteredSections = response.data.filter(
          (section) => String(section.id) === String(id)
        );

        setSections(filteredSections);
      } catch (err) {
        console.error("Error fetching sections:", err);
      }
    };

    fetchSections();
  }, [id]);

  /*
   * ---------------------------------------------------------
   * SECTION SELECTION
   * ---------------------------------------------------------
   */

  const handleSectionClick = (section) => {
    setSecId(section.sec_id);
    setSectionName(section.section_name);
    setSelectedSection(section.sec_id);
    setUserAnswers({});

    localStorage.setItem("sec_id", section.sec_id);
    localStorage.setItem("section_name", section.section_name);
  };

  /*
   * ---------------------------------------------------------
   * ANSWER SELECTION
   * ---------------------------------------------------------
   */

  const handleAnswerSelect = (
    qId,
    selectedOption,
    correctAnswer
  ) => {
    setUserAnswers((prev) => ({
      ...prev,
      [qId]: {
        selectedOption,
        correctAnswer,
      },
    }));
  };

  /*
   * ---------------------------------------------------------
   * SECTION NAVIGATION
   * ---------------------------------------------------------
   */

  const currentIndex = sections.findIndex(
    (section) => String(section.sec_id) === String(sec_id)
  );

  const prevSection =
    currentIndex > 0
      ? sections[currentIndex - 1]
      : null;

  const nextSection =
    currentIndex < sections.length - 1
      ? sections[currentIndex + 1]
      : null;

  const handleNavigation = (section) => {
    if (!section) return;

    setSecId(section.sec_id);
    setSectionName(section.section_name);
    setSelectedSection(section.sec_id);
    setUserAnswers({});

    localStorage.setItem("sec_id", section.sec_id);
    localStorage.setItem(
      "section_name",
      section.section_name
    );
  };

  /*
   * ---------------------------------------------------------
   * SCORE
   * ---------------------------------------------------------
   */

  const calculateScore = () => {
    let correctCount = 0;
    let incorrectCount = 0;

    Object.values(userAnswers).forEach(
      ({ selectedOption, correctAnswer }) => {
        if (selectedOption === correctAnswer) {
          correctCount++;
        } else {
          incorrectCount++;
        }
      }
    );

    return {
      correctCount,
      incorrectCount,
    };
  };

  const { correctCount, incorrectCount } =
    calculateScore();

  const answeredCount = Object.keys(userAnswers).length;

  const totalQuestions = questions.length;

  const progress =
    totalQuestions > 0
      ? Math.round(
          (answeredCount / totalQuestions) * 100
        )
      : 0;

  /*
   * ---------------------------------------------------------
   * SLUG
   * ---------------------------------------------------------
   */

  const createSlug = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  /*
   * ---------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------
   */

  return (
    <div className="mcq-page">

      <Header />

      {/* =====================================================
          TOP EXAM HEADER
      ====================================================== */}

      <div className="mcq-topbar">
        <Container fluid="lg">
          <div className="mcq-topbar-content">

            <div>
              <span className="mcq-eyebrow">
                MCQ PRACTICE
              </span>

              <h1>
                {topic_name || "Practice Test"}
              </h1>

              <p>
                {section_name || "Select a section"}
              </p>
            </div>

            <div className="mcq-progress-box">

              <div className="progress-info">
                <span>
                  Progress
                </span>

                <strong>
                  {answeredCount}/{totalQuestions}
                </strong>
              </div>

              <div className="mcq-progress">
                <div
                  className="mcq-progress-fill"
                  style={{
                    width: `${progress}%`,
                  }}
                />
              </div>

              <small>
                {progress}% completed
              </small>

            </div>

          </div>
        </Container>
      </div>

      <Container
        fluid="lg"
        className="mcq-main-container"
      >

        <Row className="g-4">

          {/* =================================================
              LEFT SIDEBAR
          ================================================== */}

          <Col
            lg={3}
            md={4}
            className="d-none d-md-block"
          >

            <aside className="mcq-sidebar">

              <div className="sidebar-heading">

                <span>
                  TEST SECTIONS
                </span>

                <h3>
                  {topic_name || "Sections"}
                </h3>

              </div>

              <div className="section-list">

                {sections.length > 0 ? (

                  sections.map((section, index) => {

                    const active =
                      String(selectedSection) ===
                      String(section.sec_id);

                    const answeredInSection =
                      active &&
                      Object.keys(userAnswers).length > 0;

                    return (
                      <Link
                        key={section.sec_id}
                        to={{
                          pathname: `/mcq/${createSlug(
                            section.section_name
                          )}`,
                          state: {
                            sec_id: section.sec_id,
                            id: id,
                            section_name:
                              section.section_name,
                            topic_name: topic_name,
                          },
                        }}
                        onClick={() =>
                          handleSectionClick(section)
                        }
                        className={`section-item ${
                          active ? "active" : ""
                        }`}
                      >

                        <span className="section-number">
                          {String(index + 1).padStart(
                            2,
                            "0"
                          )}
                        </span>

                        <span className="section-name">
                          {section.section_name}
                        </span>

                        {answeredInSection && (
                          <span className="section-check">
                            ✓
                          </span>
                        )}

                      </Link>
                    );
                  })

                ) : (

                  <div className="section-empty">
                    No sections available.
                  </div>

                )}

              </div>

            </aside>

          </Col>

          {/* =================================================
              MOBILE SECTION SELECTOR
          ================================================== */}

          <Col
            xs={12}
            className="d-block d-md-none"
          >

            <Dropdown className="mobile-section-dropdown">

              <Dropdown.Toggle
                variant="light"
                className="w-100"
              >
                <span>
                  ☰
                </span>

                {" "}

                {section_name ||
                  "Select Section"}
              </Dropdown.Toggle>

              <Dropdown.Menu className="w-100">

                {sections.map((section) => (

                  <Dropdown.Item
                    key={section.sec_id}
                    onClick={() =>
                      handleSectionClick(section)
                    }
                    className={
                      String(selectedSection) ===
                      String(section.sec_id)
                        ? "active"
                        : ""
                    }
                  >
                    {section.section_name}
                  </Dropdown.Item>

                ))}

              </Dropdown.Menu>

            </Dropdown>

          </Col>

          {/* =================================================
              MAIN QUESTION AREA
          ================================================== */}

          <Col lg={7} md={8} xs={12}>

            <main className="mcq-content">

              <div className="question-header">

                <div>

                  <span className="question-eyebrow">
                    CURRENT SECTION
                  </span>

                  <h2>
                    {section_name ||
                      "MCQ Questions"}
                  </h2>

                </div>

                <div className="question-count-box">

                  <strong>
                    {questions.length}
                  </strong>

                  <span>
                    Questions
                  </span>

                </div>

              </div>

              {/* LOADING */}

              {loading && (

                <div className="mcq-state-card">

                  <Spinner
                    animation="border"
                    variant="primary"
                  />

                  <h4>
                    Loading Questions
                  </h4>

                  <p>
                    Please wait while we prepare
                    your questions.
                  </p>

                </div>

              )}

              {/* ERROR */}

              {!loading && error && (

                <div className="mcq-state-card error">

                  <div className="state-icon">
                    !
                  </div>

                  <h4>
                    Unable to Load Questions
                  </h4>

                  <p>
                    {error}
                  </p>

                </div>

              )}

              {/* NO QUESTIONS */}

              {!loading &&
                !error &&
                questions.length === 0 && (

                  <div className="mcq-state-card">

                    <div className="state-icon">
                      ?
                    </div>

                    <h4>
                      No Questions Available
                    </h4>

                    <p>
                      There are currently no
                      questions in this section.
                    </p>

                  </div>

                )}

              {/* QUESTIONS */}

              {!loading &&
                !error &&
                questions.length > 0 && (

                  <div className="mcq-question-list">

                    {questions.map(
                      (question, index) => {

                        const options = [
                          question.option1,
                          question.option2,
                          question.option3,
                          question.option4,
                        ];

                        const userAnswer =
                          userAnswers[
                            question.q_id
                          ];

                        return (

                          <Card
                            key={question.q_id}
                            className="mcq-question-card"
                          >

                            {/* QUESTION TOP */}

                            <div className="question-card-top">

                              <div className="question-number-large">
                                {String(
                                  index + 1
                                ).padStart(2, "0")}
                              </div>

                              <div className="question-label">
                                QUESTION{" "}
                                {index + 1}
                              </div>

                              {question.year && (

                                <span className="question-year">
                                  {question.year}
                                </span>

                              )}

                            </div>

                            {/* QUESTION */}

                            <div className="question-text">

                              {question.question}

                            </div>

                            {/* OPTIONS */}

                            <Form className="answer-options">

                              {options.map(
                                (option, optionIndex) => {

                                  const isSelected =
                                    userAnswer
                                      ?.selectedOption ===
                                    option;

                                  const isCorrect =
                                    option ===
                                    question.answer;

                                  const isWrong =
                                    isSelected &&
                                    !isCorrect;

                                  let optionClass =
                                    "";

                                  if (
                                    userAnswer
                                  ) {

                                    if (
                                      isCorrect
                                    ) {
                                      optionClass =
                                        "correct";
                                    } else if (
                                      isWrong
                                    ) {
                                      optionClass =
                                        "wrong";
                                    } else {
                                      optionClass =
                                        "disabled";
                                    }

                                  }

                                  return (

                                    <label
                                      key={
                                        optionIndex
                                      }
                                      className={`answer-option ${optionClass}`}
                                    >

                                      <input
                                        type="radio"
                                        name={`question-${question.q_id}`}
                                        value={option}
                                        checked={
                                          isSelected
                                        }
                                        onChange={() =>
                                          handleAnswerSelect(
                                            question.q_id,
                                            option,
                                            question.answer
                                          )
                                        }
                                        disabled={
                                          !!userAnswer
                                        }
                                      />

                                      <span className="option-letter">
                                        {String.fromCharCode(
                                          65 +
                                            optionIndex
                                        )}
                                      </span>

                                      <span className="option-text">
                                        {option}
                                      </span>

                                      {userAnswer &&
                                        isCorrect && (

                                          <span className="option-result correct-result">
                                            ✓
                                          </span>

                                        )}

                                      {userAnswer &&
                                        isWrong && (

                                          <span className="option-result wrong-result">
                                            ✕
                                          </span>

                                        )}

                                    </label>

                                  );
                                }
                              )}

                            </Form>

                            {/* RESULT MESSAGE */}

                            {userAnswer && (

                              <div
                                className={
                                  userAnswer.selectedOption ===
                                  userAnswer.correctAnswer
                                    ? "answer-feedback success"
                                    : "answer-feedback danger"
                                }
                              >

                                {userAnswer.selectedOption ===
                                userAnswer.correctAnswer
                                  ? "✓ Correct Answer"
                                  : `✕ Incorrect. Correct answer: ${userAnswer.correctAnswer}`}

                              </div>

                            )}

                          </Card>

                        );

                      }
                    )}

                  </div>

                )}

              {/* RANDOM TOPICS */}

              <div className="mcq-random-topics">
                <RandomTopics />
              </div>

            </main>

          </Col>

          {/* =================================================
              RIGHT SIDEBAR
          ================================================== */}

          <Col
            lg={2}
            className="d-none d-lg-block"
          >

            <aside className="mcq-right-sidebar">

              <div className="sponsor-box">

                <span>
                  SPONSORED
                </span>

                <div className="sponsor-placeholder">
                  Advertisement
                </div>

              </div>

              <div className="quick-score">

                <span className="score-title">
                  YOUR SCORE
                </span>

                <div className="score-row">

                  <div className="score-item correct-score">

                    <strong>
                      {correctCount}
                    </strong>

                    <span>
                      Correct
                    </span>

                  </div>

                  <div className="score-item wrong-score">

                    <strong>
                      {incorrectCount}
                    </strong>

                    <span>
                      Wrong
                    </span>

                  </div>

                </div>

              </div>

            </aside>

          </Col>

        </Row>

      </Container>

      {/* =====================================================
          FIXED NAVIGATION
      ====================================================== */}

      <div className="mcq-bottom-navigation">

        <div className="bottom-inner">

          <Button
            className="navigation-button previous"
            disabled={!prevSection}
            onClick={() =>
              handleNavigation(prevSection)
            }
          >
            <span>
              ←
            </span>

            <div>
              <small>
                PREVIOUS
              </small>

              <strong>
                {prevSection
                  ? prevSection.section_name
                  : "First Section"}
              </strong>
            </div>

          </Button>

          <div className="bottom-score">

            <div className="mini-score correct">
              <strong>
                {correctCount}
              </strong>

              <span>
                Correct
              </span>
            </div>

            <div className="mini-score wrong">
              <strong>
                {incorrectCount}
              </strong>

              <span>
                Wrong
              </span>
            </div>

          </div>

          <Button
            className="navigation-button next"
            disabled={!nextSection}
            onClick={() =>
              handleNavigation(nextSection)
            }
          >

            <div>
              <small>
                NEXT
              </small>

              <strong>
                {nextSection
                  ? nextSection.section_name
                  : "Last Section"}
              </strong>
            </div>

            <span>
              →
            </span>

          </Button>

        </div>

      </div>

      <Footer />

    </div>
  );
};

export default MCQ;